import { UserListItemDto as IUserListItemDto } from './userConfig'

import { BaseTierDto, BaseUserTierDto } from './finance/financeConfig'
import { ReplicationDto } from './emailConfig'
import { Billable } from './registration/timeConfig'
import { DimensionPivotValues } from '../components/Form/Accounting/DimensionFields'
import { FileModuleDto } from '../hooks/module/useFileModule'
import { AttributeEntityDto } from '../hooks/useAttributes'
import { FileSurchargeDto } from '../components/Form/File/Surcharge/FileSurchargeList'
import { FileRateType, FileScaffoldAuthorizationDto } from '../context/GlobalContext'

export interface IReferenceDto {
    id: string
    reference: string
    name: string
    ownerId: string
}

export interface IOverviewItemDto {
    id: string
    owner: IUserListItemDto
    executiveIds?: string[]
    reference: string
    name: string
    normalizedName: string
    documentRootId: string
    sharedDocumentRootURL?: string
    currency: string
    fileRateType: FileRateType
    rate: string
    language: string
    status: FileStatus
    workflows: string[]

    pinned?: boolean
    lastAccess?: string
}

export type SummaryDto = IOverviewItemDto & {
    description: string
    notification?: string
    invoiceEmail?: string
}

export type FileMutationDto<T extends BaseUserTierDto = BaseUserTierDto> = RateDto<T> &
    AttributeEntityDto & {
        created?: string
        organisationProfileKey?: string
        status: FileStatus
        reference: string
        name: string
        description?: string
        notification?: string
        ownerId: string
        kyc?: boolean
        crmRelationId?: string
        executiveIds?: string[]
        documentRootId?: string
        sharedDocumentRootURL?: string
        language: string
        vatType: string
        paymentCurrency?: string
        dateOfIncident?: string
        archiveLocation?: string
        invoiceName?: string
        invoiceEmail?: string
        unit?: string
        workflows?: string[]
        accounting?: DimensionPivotValues
        authorization?: FileScaffoldAuthorizationDto
        modules?: FileModuleDto[]
        surcharges?: FileSurchargeDto[]
    }

export type RateDto<T extends BaseTierDto> = {
    billable?: Billable
    fileRateType: FileRateType
    currency: string
    rate?: string
    tiers?: T[]
}

export type ScaffoldDto = Pick<
    FileMutationDto,
    | 'accounting'
    | 'authorization'
    | 'billable'
    | 'currency'
    | 'fileRateType'
    | 'language'
    | 'paymentCurrency'
    | 'rate'
    | 'reference'
    | 'status'
    | 'surcharges'
    | 'unit'
    | 'vatType'
    | 'workflows'
>

export interface IFileDto extends IOverviewItemDto, Omit<AttributeEntityDto, 'currency'> {
    organisationProfileKey?: string
    description: string
    notification?: string
    archiveLocation: string
    invoiceName: string
    invoiceEmail: string
    vatType: string
    dateOfIncident: string
    unit?: string
    accounting?: DimensionPivotValues
    modules?: FileModuleDto[]
    paymentCurrency?: string
    kyc: boolean
    crmRelationId?: string
    mailFolder?: ReplicationDto // Contains the user-specific replication folders for this file.
    replication?: ReplicationDto // Contains the shared mailbox-specific replication folders for this file.
}

export enum FileStatus {
    Open = 'OPEN',
    Archived = 'ARCHIVED',
}

export enum FileAction {
    GetMutation = 'GET_MUTATION_FILE',
    GetList = 'GET_FILE_LIST',
    GetEmails = 'GET_FILE_EMAILS',
    GetNotes = 'GET_FILE_NOTES',
    Get = 'GET_FILE',
    Add = 'ADD_FILE',
    Update = 'UPDATE_FILE',
    Delete = 'DELETE_FILE',
    ResetCurrent = 'RESET_CURRENT_FILE',
    ResetList = 'RESET_FILE_LIST',
    ResetEmails = 'RESET_FILE_EMAILS',
    ResetNotes = 'RESET_FILE_NOTES',
}
export type BudgetMonitoringDto = {
    fileId: string

    billedHours: number
    billedHoursPrice: string

    outstandingHours: number
    outstandingHoursPrice: string

    billedTravelsPrice: string
    outstandingTravelsPrice: string

    billedDisbursementsPrice: string
    outstandingDisbursementsPrice: string
}
